import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import type { Project, ShortlistEntry, Space } from "@scenix/core";
import { ApiError, api } from "../lib/api";
import { MomentPlayer } from "./MomentPlayer";
import { timecode } from "../lib/scrub";

interface ShortlistPanelProps {
  spaceId: Space["id"];
  projectId: Project["id"];
  /**
   * Gathering and promoting are both Editor-only on the API, because both are
   * decisions about what the video is rather than observations about the
   * material (ADR 000051). A viewer sees the shortlist and none of the verbs.
   */
  mayEdit: boolean;
}

/**
 * The shortlist: moments somebody sent to this project (ADR 000051).
 *
 * Entries name a moment rather than copying it, and the server resolves them on
 * read, so a range corrected back on the asset reads correctly here without
 * anything being re-sent. An entry whose moment was deleted since is filtered
 * server-side rather than shown as a hole.
 *
 * There is no reordering here on purpose. Entries are ordered by when they were
 * added, and arranging things is what the timeline is for; a second ordered
 * surface would be a worse version of one that already exists.
 */
export function ShortlistPanel({ spaceId, projectId, mayEdit }: ShortlistPanelProps) {
  const queryClient = useQueryClient();
  const [failure, setFailure] = useState<string | null>(null);
  // Which entry is open, if any. One at a time: a shortlist of ten all playing
  // at once is not a way to decide between them.
  const [playing, setPlaying] = useState<string | null>(null);

  const shortlistKey = ["shortlist", spaceId, projectId];

  const shortlist = useQuery({
    queryKey: shortlistKey,
    queryFn: () => api.listShortlist(spaceId, projectId),
  });

  const promote = useMutation({
    mutationFn: (entry: ShortlistEntry) =>
      api.promoteShortlistEntry(spaceId, projectId, entry.assetId, entry.momentId),
    onSuccess: () => {
      setFailure(null);
      // The segment lands at the end of the chain, so the timeline beside this
      // has to re-read or the new one is invisible until a refresh.
      void queryClient.invalidateQueries({ queryKey: ["timeline", spaceId, projectId] });
    },
    onError: (error: unknown) => setFailure(describe(error)),
  });

  const remove = useMutation({
    mutationFn: (entry: ShortlistEntry) =>
      api.removeFromShortlist(spaceId, projectId, entry.assetId, entry.momentId),
    onSuccess: () => {
      setFailure(null);
      void queryClient.invalidateQueries({ queryKey: shortlistKey });
    },
    onError: (error: unknown) => setFailure(describe(error)),
  });

  const entries = shortlist.data ?? [];

  return (
    <section>
      <h2 className="section-head">Shortlist</h2>

      {failure && (
        <p role="alert" className="error">
          {failure}
        </p>
      )}

      {shortlist.isPending ? (
        <p className="muted">Reading the shortlist…</p>
      ) : entries.length === 0 ? (
        <p className="muted">
          Nothing gathered yet. Annotate a moment on a piece of{" "}
          <Link to={`/spaces/${spaceId}/content`}>content</Link> and send it here to build a cut
          from it.
        </p>
      ) : (
        <ul className="shortlist">
          {entries.map((entry) => {
            const moment = entry.moment;
            // The server filters dangling entries, so this is belt and braces
            // rather than a case a reader is expected to hit.
            if (!moment) return null;
            const busy =
              (promote.isPending && promote.variables?.momentId === entry.momentId) ||
              (remove.isPending && remove.variables?.momentId === entry.momentId);

            const key = `${entry.assetId}:${entry.momentId}`;

            return (
              <li key={key}>
                {/*
                 * Playing it is what somebody is here to do: a shortlist is a
                 * pile of moments being decided between, and deciding means
                 * watching them. Following a link to the asset page loses the
                 * pile, which is the thing being decided.
                 *
                 * A button rather than a hover, because a row of videos that
                 * start themselves as the pointer crosses them is not help.
                 */}
                <button
                  type="button"
                  className="shortlist-still"
                  aria-label={`Play ${moment.title}`}
                  aria-expanded={playing === key}
                  onClick={() => setPlaying((open) => (open === key ? null : key))}
                >
                  {moment.thumbnailPath ? (
                    <img
                      src={api.momentThumbnailUrl(spaceId, entry.assetId, entry.momentId)}
                      alt=""
                    />
                  ) : (
                    <span className="moment-still is-empty" />
                  )}
                  <span className="shortlist-play" aria-hidden="true">
                    ▶
                  </span>
                </button>

                <span className="shortlist-body">
                  <strong>{moment.title}</strong>
                  {moment.authorKind === "assistant" && (
                    <>
                      {/* JSX emits no space around an element on its own line,
                          so without this the badge reads as the last word of
                          the title (issue #310). */}{" "}
                      <span className="badge" title="Written by an agent, not a person">
                        agent
                      </span>
                    </>
                  )}
                  <span className="muted">
                    {timecode(moment.range.startSec)} to {timecode(moment.range.endSec)}
                  </span>
                  {moment.tags.length > 0 && (
                    <span className="moment-tags">
                      {moment.tags.map((tag) => (
                        <span className="tag" key={tag}>
                          {tag}
                        </span>
                      ))}
                    </span>
                  )}
                </span>

                {mayEdit && (
                  <span className="row">
                    <button type="button" disabled={busy} onClick={() => promote.mutate(entry)}>
                      Add to timeline
                    </button>
                    <button
                      type="button"
                      className="ghost danger"
                      disabled={busy}
                      onClick={() => remove.mutate(entry)}
                    >
                      Remove
                    </button>
                  </span>
                )}

                {playing === key && (
                  <MomentPlayer spaceId={spaceId} assetId={entry.assetId} range={moment.range} />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.userMessage;
  return error instanceof Error ? error.message : "Something went wrong.";
}
